import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import { deleteUser, fetchUser, updateUserPrivileges, updateUserProfile } from '@/features/users/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

/**
 * Ports includes/user_details.html: profile fields (firstname/lastname/
 * phone/company/password), a separate privileges form
 * (is_admin/is_super_user/is_staff/is_client — gated by
 * users.privileges.manage, same as the backend route), and delete (admin
 * only). The legacy template disables the email field and only shows the
 * profile-save button when `current_user.email == user.email or
 * current_user.is_super_user` — same gate applied here.
 *
 * is_staff was missing from the privileges form entirely (no checkbox, and
 * the backend endpoint didn't accept it either) - this was the only one of
 * the app's four role flags (Admin/Super User/Staff/Client) this page
 * couldn't grant, even though fetchUser already returns is_staff and the
 * separate /privileges page can set it via a different, coarser endpoint.
 */
export default function UserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>()
  const { user: currentUser, hasPermission } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const userQuery = useQuery({
    queryKey: ['user', userId],
    queryFn: () => fetchUser(userId),
    enabled: Boolean(userId),
  })

  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuperUser, setIsSuperUser] = useState(false)
  const [isStaff, setIsStaff] = useState(false)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    if (!userQuery.data) return
    setFirstname(userQuery.data.firstname ?? '')
    setLastname(userQuery.data.lastname ?? '')
    setPhone(userQuery.data.phone ?? '')
    setIsAdmin(userQuery.data.is_admin)
    setIsSuperUser(userQuery.data.is_super_user)
    setIsStaff(userQuery.data.is_staff)
    setIsClient(userQuery.data.is_client)
  }, [userQuery.data])

  const profileMutation = useMutation({
    mutationFn: () => updateUserProfile(userId, { firstname, lastname, phone, password: password || undefined }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['user', userId], updated)
      setPassword('')
    },
  })

  const privilegesMutation = useMutation({
    mutationFn: () =>
      updateUserPrivileges(userId, { is_admin: isAdmin, is_super_user: isSuperUser, is_staff: isStaff, is_client: isClient }),
    onSuccess: (updated) => queryClient.setQueryData(['user', userId], updated),
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteUser(userId),
    onSuccess: () => navigate('/users', { replace: true }),
  })

  if (userQuery.isLoading) return <LoadingState label="Loading user…" />
  if (userQuery.isError) {
    return (
      <ErrorState
        message={userQuery.error instanceof Error ? userQuery.error.message : 'Failed to load user.'}
        onRetry={() => userQuery.refetch()}
      />
    )
  }
  const user = userQuery.data
  if (!user) return null

  const canEditProfile = currentUser?.email === user.email || currentUser?.is_super_user
  const canManagePrivileges = hasPermission('users.privileges.manage')
  const canDelete = currentUser?.is_admin

  return (
    <div className="max-w-xl space-y-6">
      <h1 className="text-xl font-semibold text-text-primary">
        {user.firstname} {user.lastname}
      </h1>

      <section className="rounded border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">Profile</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            profileMutation.mutate()
          }}
          className="space-y-3"
        >
          <Field label="First name" value={firstname} onChange={setFirstname} disabled={!canEditProfile} />
          <Field label="Last name" value={lastname} onChange={setLastname} disabled={!canEditProfile} />
          <Field label="Email" value={user.email} onChange={() => {}} disabled />
          <Field label="Phone" value={phone} onChange={setPhone} disabled={!canEditProfile} />
          <Field
            label="New password (leave blank to keep unchanged)"
            value={password}
            onChange={setPassword}
            disabled={!canEditProfile}
            type="password"
          />
          {canEditProfile && (
            <button
              type="submit"
              disabled={profileMutation.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {profileMutation.isPending ? 'Saving…' : 'Save Profile'}
            </button>
          )}
        </form>
      </section>

      {canManagePrivileges && (
        <section className="rounded border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-text-primary">Privileges</h2>
          <div className="space-y-2 text-sm">
            <Checkbox label="Admin" checked={isAdmin} onChange={setIsAdmin} />
            <Checkbox label="Super User" checked={isSuperUser} onChange={setIsSuperUser} />
            <Checkbox label="Staff" checked={isStaff} onChange={setIsStaff} />
            <Checkbox label="Client" checked={isClient} onChange={setIsClient} />
          </div>
          <button
            type="button"
            onClick={() => privilegesMutation.mutate()}
            disabled={privilegesMutation.isPending}
            className="mt-3 rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {privilegesMutation.isPending ? 'Saving…' : 'Update Privileges'}
          </button>
        </section>
      )}

      {canDelete && (
        <section className="rounded border border-error p-4">
          <h2 className="mb-2 text-sm font-semibold text-error">Danger zone</h2>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete user ${user.email}? This cannot be undone.`)) deleteMutation.mutate()
            }}
            disabled={deleteMutation.isPending}
            className="rounded border border-error px-3 py-1.5 text-sm font-medium text-error hover:bg-error hover:text-white disabled:opacity-50"
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete User'}
          </button>
        </section>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  disabled,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  type?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-xs text-text-secondary">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary disabled:opacity-60"
      />
    </div>
  )
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-text-primary">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}
